import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, Trash2, Pencil, Tag, Copy, Check } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import AdminLayout from '../../components/admin/AdminLayout';

const emptyForm = {
  code: '', discount_type: 'percent', discount_value: 10, min_ride_amount: 0,
  max_uses: '', max_uses_per_user: 1, expires_at: '', enabled: true, description: '',
};

const AdminPromos = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [copied, setCopied] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const api = (await import('../../utils/api')).default;
      const { data } = await api.get('/admin/promos');
      setPromos(data.items || []);
    } catch {
      toast({ title: 'Failed to load promos', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setShowDialog(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      code: p.code, discount_type: p.discount_type, discount_value: p.discount_value,
      min_ride_amount: p.min_ride_amount || 0, max_uses: p.max_uses ?? '',
      max_uses_per_user: p.max_uses_per_user || 1,
      expires_at: p.expires_at ? p.expires_at.slice(0, 16) : '',
      enabled: p.enabled, description: p.description || '',
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.code.trim()) { toast({ title: 'Code required', variant: 'destructive' }); return; }
    const payload = {
      code: form.code.toUpperCase().trim(),
      discount_type: form.discount_type,
      discount_value: parseFloat(form.discount_value),
      min_ride_amount: parseFloat(form.min_ride_amount) || 0,
      max_uses: form.max_uses === '' ? null : parseInt(form.max_uses, 10),
      max_uses_per_user: parseInt(form.max_uses_per_user, 10) || 1,
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      enabled: form.enabled,
      description: form.description.trim() || null,
    };
    try {
      const api = (await import('../../utils/api')).default;
      if (editing) {
        const { code, ...rest } = payload;
        await api.put(`/admin/promos/${editing}`, rest);
        toast({ title: 'Promo updated' });
      } else {
        await api.post('/admin/promos', payload);
        toast({ title: 'Promo created' });
      }
      setShowDialog(false); fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Save failed', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promo?')) return;
    try {
      const api = (await import('../../utils/api')).default;
      await api.delete(`/admin/promos/${id}`);
      toast({ title: 'Deleted' });
      fetchData();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const toggleEnabled = async (p) => {
    try {
      const api = (await import('../../utils/api')).default;
      await api.put(`/admin/promos/${p.id}`, { enabled: !p.enabled });
      fetchData();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleCopy = async (code) => {
    await navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(''), 1500);
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="admin-promos-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Tag className="h-6 w-6 text-pink-600" /> Promo Codes</h1>
            <p className="text-sm text-gray-500">Discount codes that riders can apply at booking. Track usage and limits.</p>
          </div>
          <Button onClick={openNew} className="bg-pink-600 hover:bg-pink-700" data-testid="add-promo-btn">
            <Plus className="h-4 w-4 mr-1" /> New Promo
          </Button>
        </div>

        {loading && <p className="text-gray-500">Loading...</p>}
        {!loading && promos.length === 0 && (
          <Card className="p-8 text-center text-gray-500">
            <Tag className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No promo codes yet. Click "New Promo" to create one.</p>
          </Card>
        )}

        <div className="space-y-3" data-testid="promos-list">
          {promos.map(p => (
            <Card key={p.id} className="p-4">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-mono font-bold text-pink-600">{p.code}</h3>
                    <button onClick={() => handleCopy(p.code)} className="text-gray-400 hover:text-pink-600" title="Copy">
                      {copied === p.code ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {p.enabled ? 'Active' : 'Disabled'}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-pink-100 text-pink-700 font-bold">
                      {p.discount_type === 'percent' ? `${p.discount_value}% off` : `K${p.discount_value} off`}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Used {p.uses}{p.max_uses ? ` / ${p.max_uses}` : ''} times · {p.max_uses_per_user || 1} per user
                    {p.min_ride_amount > 0 && ` · Min K${p.min_ride_amount}`}
                    {p.expires_at && ` · Expires ${new Date(p.expires_at).toLocaleDateString()}`}
                  </div>
                  {p.description && <p className="text-xs text-gray-500 mt-1 italic">{p.description}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={p.enabled} onCheckedChange={() => toggleEnabled(p)} />
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(p.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-md" data-testid="promo-dialog">
            <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Promo</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Code</Label>
                <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} disabled={!!editing} placeholder="WELCOME10" data-testid="promo-code-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Discount type</Label>
                  <select value={form.discount_type} onChange={e => setForm({ ...form, discount_type: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                    <option value="percent">Percent (%)</option>
                    <option value="flat">Flat (K)</option>
                  </select>
                </div>
                <div>
                  <Label>Value</Label>
                  <Input type="number" step="0.01" value={form.discount_value} onChange={e => setForm({ ...form, discount_value: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Min ride amount (K)</Label>
                  <Input type="number" step="0.01" value={form.min_ride_amount} onChange={e => setForm({ ...form, min_ride_amount: e.target.value })} />
                </div>
                <div>
                  <Label>Per-user limit</Label>
                  <Input type="number" min="1" value={form.max_uses_per_user} onChange={e => setForm({ ...form, max_uses_per_user: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Total uses (blank=unlimited)</Label>
                  <Input type="number" min="0" value={form.max_uses} onChange={e => setForm({ ...form, max_uses: e.target.value })} />
                </div>
                <div>
                  <Label>Expires at</Label>
                  <Input type="datetime-local" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Description (shown to riders)</Label>
                <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="10% off your first ride" />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
                <Label className="!mb-0">Enabled</Label>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button className="flex-1 bg-pink-600 hover:bg-pink-700" onClick={handleSave} data-testid="promo-save-btn">Save</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminPromos;
